import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { 
  Calendar, Users, FileText, Search, Plus, 
  ChevronRight, Clock, Phone, Mail, User,
  Heart, Thermometer, Activity, Scale, Ruler,
  Stethoscope, Pill, ClipboardList, Save, X
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DoctorDashboard = () => {
  const navigate = useNavigate();
  const { user, token, isDoctor, isAdmin, isAuthenticated } = useAuth();
  
  const [stats, setStats] = useState({});
  const [appointments, setAppointments] = useState([]);
  const [patients, setPatients] = useState([]);
  const [patientSearch, setPatientSearch] = useState('');
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [medicalRecords, setMedicalRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Modals
  const [showPatientModal, setShowPatientModal] = useState(false);
  const [showRecordModal, setShowRecordModal] = useState(false);
  const [editingPatient, setEditingPatient] = useState(null);
  
  // Forms
  const [patientForm, setPatientForm] = useState({
    first_name: '', last_name: '', phone: '', email: '',
    birth_date: '', gender: '', blood_type: '',
    allergies: '', chronic_conditions: '',
    emergency_contact: '', emergency_phone: '', notes: ''
  });
  
  const [recordForm, setRecordForm] = useState({
    weight: '', height: '', blood_pressure_systolic: '', blood_pressure_diastolic: '',
    heart_rate: '', temperature: '', oxygen_saturation: '',
    symptoms: '', diagnosis: '', treatment: '', prescription: '',
    lab_results: '', notes: '', follow_up_date: ''
  });

  useEffect(() => {
    if (isAuthenticated && (isDoctor || isAdmin)) {
      fetchData();
    }
  }, [isAuthenticated, isDoctor, isAdmin]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      // Fetch stats
      if (isDoctor) {
        const statsRes = await axios.get(`${API}/doctor/stats`, { headers });
        setStats(statsRes.data);
        
        const aptsRes = await axios.get(`${API}/doctor/my-appointments`, { headers });
        setAppointments(aptsRes.data);
      }
      
      // Fetch patients
      const patientsRes = await axios.get(`${API}/patients`, { headers });
      setPatients(patientsRes.data);
      
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const searchPatients = async () => {
    try {
      const res = await axios.get(`${API}/patients?search=${patientSearch}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPatients(res.data);
    } catch (error) {
      toast.error('Error al buscar pacientes');
    }
  };

  const fetchMedicalRecords = async (patientId) => {
    try {
      const res = await axios.get(`${API}/medical-records/${patientId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMedicalRecords(res.data);
    } catch (error) {
      toast.error('Error al cargar historial');
    }
  };

  const selectPatient = (patient) => {
    setSelectedPatient(patient);
    fetchMedicalRecords(patient.id);
  };

  const openPatientModal = (patient = null) => {
    if (patient) {
      setEditingPatient(patient);
      setPatientForm({
        first_name: patient.first_name || '',
        last_name: patient.last_name || '',
        phone: patient.phone || '',
        email: patient.email || '',
        birth_date: patient.birth_date || '',
        gender: patient.gender || '',
        blood_type: patient.blood_type || '',
        allergies: patient.allergies || '',
        chronic_conditions: patient.chronic_conditions || '',
        emergency_contact: patient.emergency_contact || '',
        emergency_phone: patient.emergency_phone || '',
        notes: patient.notes || ''
      });
    } else {
      setEditingPatient(null);
      setPatientForm({
        first_name: '', last_name: '', phone: '', email: '',
        birth_date: '', gender: '', blood_type: '',
        allergies: '', chronic_conditions: '',
        emergency_contact: '', emergency_phone: '', notes: ''
      });
    }
    setShowPatientModal(true);
  };

  const savePatient = async () => {
    if (!patientForm.first_name || !patientForm.last_name) {
      toast.error('Nombre y apellido son requeridos');
      return;
    }

    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      if (editingPatient) {
        await axios.put(`${API}/patients/${editingPatient.id}`, patientForm, { headers });
        toast.success('Paciente actualizado');
      } else {
        const res = await axios.post(`${API}/patients`, patientForm, { headers });
        setSelectedPatient(res.data);
        toast.success('Paciente creado');
      }
      
      setShowPatientModal(false);
      fetchData();
    } catch (error) {
      toast.error('Error al guardar paciente');
    }
  };

  const openRecordModal = () => {
    setRecordForm({
      weight: '', height: '', blood_pressure_systolic: '', blood_pressure_diastolic: '',
      heart_rate: '', temperature: '', oxygen_saturation: '',
      symptoms: '', diagnosis: '', treatment: '', prescription: '',
      lab_results: '', notes: '', follow_up_date: ''
    });
    setShowRecordModal(true);
  };

  const saveRecord = async () => {
    if (!selectedPatient) {
      toast.error('Selecciona un paciente primero');
      return;
    }

    try {
      const data = {
        patient_id: selectedPatient.id,
        weight: recordForm.weight ? parseFloat(recordForm.weight) : null,
        height: recordForm.height ? parseFloat(recordForm.height) : null,
        blood_pressure_systolic: recordForm.blood_pressure_systolic ? parseInt(recordForm.blood_pressure_systolic) : null,
        blood_pressure_diastolic: recordForm.blood_pressure_diastolic ? parseInt(recordForm.blood_pressure_diastolic) : null,
        heart_rate: recordForm.heart_rate ? parseInt(recordForm.heart_rate) : null,
        temperature: recordForm.temperature ? parseFloat(recordForm.temperature) : null,
        oxygen_saturation: recordForm.oxygen_saturation ? parseInt(recordForm.oxygen_saturation) : null,
        symptoms: recordForm.symptoms || null,
        diagnosis: recordForm.diagnosis || null,
        treatment: recordForm.treatment || null,
        prescription: recordForm.prescription || null,
        lab_results: recordForm.lab_results || null,
        notes: recordForm.notes || null,
        follow_up_date: recordForm.follow_up_date || null
      };

      await axios.post(`${API}/medical-records`, data, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Consulta guardada');
      setShowRecordModal(false);
      fetchMedicalRecords(selectedPatient.id);
    } catch (error) {
      toast.error('Error al guardar consulta');
    }
  };

  if (!isAuthenticated || (!isDoctor && !isAdmin)) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <Stethoscope className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h1 className="text-xl font-semibold text-slate-700">Acceso Restringido</h1>
          <p className="text-slate-500 mt-2">Esta sección es solo para doctores</p>
          <Button onClick={() => navigate('/')} className="mt-4">Volver al inicio</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-8">
      <div className="container-app">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-slate-900">Panel del Doctor</h1>
          <p className="text-slate-500">Gestiona tus pacientes y consultas</p>
        </div>

        {/* Stats */}
        {isDoctor && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-white rounded-2xl p-4 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                  <Calendar className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{stats.today_appointments || 0}</p>
                  <p className="text-xs text-slate-500">Citas hoy</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-2xl p-4 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center">
                  <Users className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{stats.total_patients || 0}</p>
                  <p className="text-xs text-slate-500">Pacientes</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-2xl p-4 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center">
                  <FileText className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{stats.total_records || 0}</p>
                  <p className="text-xs text-slate-500">Consultas</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-2xl p-4 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center">
                  <ClipboardList className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{stats.total_appointments || 0}</p>
                  <p className="text-xs text-slate-500">Total citas</p>
                </div>
              </div>
            </div>
          </div>
        )}

        <Tabs defaultValue="patients" className="space-y-6">
          <TabsList className="bg-white p-1 rounded-xl shadow-sm">
            <TabsTrigger value="patients" className="rounded-lg">
              <Users className="w-4 h-4 mr-2" />
              Pacientes
            </TabsTrigger>
            {isDoctor && (
              <TabsTrigger value="appointments" className="rounded-lg">
                <Calendar className="w-4 h-4 mr-2" />
                Mis Citas
              </TabsTrigger>
            )}
          </TabsList>

          {/* Patients Tab */}
          <TabsContent value="patients">
            <div className="grid md:grid-cols-3 gap-6">
              {/* Patient List */}
              <div className="bg-white rounded-2xl p-4 shadow-sm">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold">Pacientes</h3>
                  <Button size="sm" onClick={() => openPatientModal()} data-testid="add-patient-btn">
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                
                <div className="flex gap-2 mb-4">
                  <Input
                    placeholder="Buscar paciente..."
                    value={patientSearch}
                    onChange={(e) => setPatientSearch(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && searchPatients()}
                  />
                  <Button variant="outline" size="icon" onClick={searchPatients}>
                    <Search className="w-4 h-4" />
                  </Button>
                </div>

                <div className="space-y-2 max-h-[500px] overflow-y-auto">
                  {patients.map((patient) => (
                    <div
                      key={patient.id}
                      onClick={() => selectPatient(patient)}
                      className={`p-3 rounded-xl cursor-pointer transition-colors ${
                        selectedPatient?.id === patient.id 
                          ? 'bg-blue-50 border-2 border-blue-500' 
                          : 'bg-slate-50 hover:bg-slate-100'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-slate-200 rounded-full flex items-center justify-center">
                          <User className="w-5 h-5 text-slate-500" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-slate-900 truncate">
                            {patient.first_name} {patient.last_name}
                          </p>
                          <p className="text-xs text-slate-500">{patient.phone || 'Sin teléfono'}</p>
                        </div>
                        <ChevronRight className="w-4 h-4 text-slate-400" />
                      </div>
                    </div>
                  ))}
                  {patients.length === 0 && (
                    <p className="text-center text-slate-500 py-8">No hay pacientes</p>
                  )}
                </div>
              </div>

              {/* Patient Detail & Records */}
              <div className="md:col-span-2 space-y-4">
                {selectedPatient ? (
                  <>
                    {/* Patient Info Card */}
                    <div className="bg-white rounded-2xl p-6 shadow-sm">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center gap-4">
                          <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center">
                            <User className="w-8 h-8 text-blue-600" />
                          </div>
                          <div>
                            <h2 className="text-xl font-bold text-slate-900">
                              {selectedPatient.first_name} {selectedPatient.last_name}
                            </h2>
                            <div className="flex items-center gap-4 mt-1 text-sm text-slate-500">
                              {selectedPatient.phone && (
                                <span className="flex items-center gap-1">
                                  <Phone className="w-3 h-3" /> {selectedPatient.phone}
                                </span>
                              )}
                              {selectedPatient.email && (
                                <span className="flex items-center gap-1">
                                  <Mail className="w-3 h-3" /> {selectedPatient.email}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <Button variant="outline" size="sm" onClick={() => openPatientModal(selectedPatient)}>
                          Editar
                        </Button>
                      </div>

                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        {selectedPatient.birth_date && (
                          <div>
                            <p className="text-slate-500">Nacimiento</p>
                            <p className="font-medium">{selectedPatient.birth_date}</p>
                          </div>
                        )}
                        {selectedPatient.gender && (
                          <div>
                            <p className="text-slate-500">Género</p>
                            <p className="font-medium">{selectedPatient.gender}</p>
                          </div>
                        )}
                        {selectedPatient.blood_type && (
                          <div>
                            <p className="text-slate-500">Tipo de sangre</p>
                            <p className="font-medium">{selectedPatient.blood_type}</p>
                          </div>
                        )}
                        {selectedPatient.allergies && (
                          <div className="col-span-2">
                            <p className="text-slate-500">Alergias</p>
                            <p className="font-medium text-red-600">{selectedPatient.allergies}</p>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Medical Records */}
                    <div className="bg-white rounded-2xl p-6 shadow-sm">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="font-semibold">Historial de Consultas</h3>
                        <Button onClick={openRecordModal} className="btn-primary">
                          <Plus className="w-4 h-4 mr-2" />
                          Nueva Consulta
                        </Button>
                      </div>

                      <div className="space-y-4">
                        {medicalRecords.map((record) => (
                          <div key={record.id} className="p-4 bg-slate-50 rounded-xl">
                            <div className="flex items-center justify-between mb-3">
                              <span className="text-sm text-slate-500">
                                {new Date(record.created_at).toLocaleDateString('es-MX', {
                                  year: 'numeric', month: 'long', day: 'numeric'
                                })}
                              </span>
                              <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full">
                                Dr. {record.doctor_name}
                              </span>
                            </div>

                            {/* Vital Signs */}
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3 text-sm">
                              {record.weight && (
                                <div className="flex items-center gap-2">
                                  <Scale className="w-4 h-4 text-slate-400" />
                                  <span>{record.weight} kg</span>
                                </div>
                              )}
                              {record.height && (
                                <div className="flex items-center gap-2">
                                  <Ruler className="w-4 h-4 text-slate-400" />
                                  <span>{record.height} cm</span>
                                </div>
                              )}
                              {record.blood_pressure_systolic && (
                                <div className="flex items-center gap-2">
                                  <Activity className="w-4 h-4 text-slate-400" />
                                  <span>{record.blood_pressure_systolic}/{record.blood_pressure_diastolic} mmHg</span>
                                </div>
                              )}
                              {record.temperature && (
                                <div className="flex items-center gap-2">
                                  <Thermometer className="w-4 h-4 text-slate-400" />
                                  <span>{record.temperature}°C</span>
                                </div>
                              )}
                              {record.heart_rate && (
                                <div className="flex items-center gap-2">
                                  <Heart className="w-4 h-4 text-slate-400" />
                                  <span>{record.heart_rate} bpm</span>
                                </div>
                              )}
                            </div>

                            {record.diagnosis && (
                              <div className="mb-2">
                                <p className="text-xs text-slate-500 uppercase">Diagnóstico</p>
                                <p className="font-medium">{record.diagnosis}</p>
                              </div>
                            )}

                            {record.prescription && (
                              <div className="mb-2 p-3 bg-green-50 rounded-lg">
                                <p className="text-xs text-green-600 uppercase flex items-center gap-1">
                                  <Pill className="w-3 h-3" /> Receta
                                </p>
                                <p className="text-sm whitespace-pre-wrap">{record.prescription}</p>
                              </div>
                            )}

                            {record.notes && (
                              <div className="text-sm text-slate-600">
                                <p className="text-xs text-slate-500 uppercase">Notas</p>
                                <p>{record.notes}</p>
                              </div>
                            )}
                          </div>
                        ))}
                        {medicalRecords.length === 0 && (
                          <p className="text-center text-slate-500 py-8">
                            No hay consultas registradas
                          </p>
                        )}
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="bg-white rounded-2xl p-12 shadow-sm text-center">
                    <Users className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                    <h3 className="font-semibold text-slate-700">Selecciona un paciente</h3>
                    <p className="text-slate-500 mt-1">
                      Elige un paciente de la lista para ver su historial
                    </p>
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

          {/* Appointments Tab */}
          {isDoctor && (
            <TabsContent value="appointments">
              <div className="bg-white rounded-2xl p-6 shadow-sm">
                <h3 className="font-semibold mb-4">Mis Citas Programadas</h3>
                <div className="space-y-3">
                  {appointments.map((apt) => (
                    <div key={apt.id} className="p-4 bg-slate-50 rounded-xl flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                          <Calendar className="w-6 h-6 text-blue-600" />
                        </div>
                        <div>
                          <p className="font-semibold">{apt.patient_name || 'Paciente'}</p>
                          <p className="text-sm text-slate-500">
                            {apt.date} - {apt.time} | {apt.consultation_type}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                          apt.status === 'confirmed' ? 'bg-green-100 text-green-700' :
                          apt.status === 'completed' ? 'bg-blue-100 text-blue-700' :
                          'bg-amber-100 text-amber-700'
                        }`}>
                          {apt.status === 'confirmed' ? 'Confirmada' :
                           apt.status === 'completed' ? 'Completada' : 'Pendiente'}
                        </span>
                        {apt.meet_link && (
                          <a 
                            href={apt.meet_link} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="block text-xs text-blue-600 mt-1 hover:underline"
                          >
                            Unirse a videollamada
                          </a>
                        )}
                      </div>
                    </div>
                  ))}
                  {appointments.length === 0 && (
                    <p className="text-center text-slate-500 py-8">No tienes citas programadas</p>
                  )}
                </div>
              </div>
            </TabsContent>
          )}
        </Tabs>
      </div>

      {/* Patient Modal */}
      <Dialog open={showPatientModal} onOpenChange={setShowPatientModal}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingPatient ? 'Editar Paciente' : 'Nuevo Paciente'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Nombre *</Label>
                <Input
                  value={patientForm.first_name}
                  onChange={(e) => setPatientForm({...patientForm, first_name: e.target.value})}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Apellido *</Label>
                <Input
                  value={patientForm.last_name}
                  onChange={(e) => setPatientForm({...patientForm, last_name: e.target.value})}
                  className="mt-1"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Teléfono</Label>
                <Input
                  value={patientForm.phone}
                  onChange={(e) => setPatientForm({...patientForm, phone: e.target.value})}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Email</Label>
                <Input
                  value={patientForm.email}
                  onChange={(e) => setPatientForm({...patientForm, email: e.target.value})}
                  className="mt-1"
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Fecha de Nacimiento</Label>
                <Input
                  type="date"
                  value={patientForm.birth_date}
                  onChange={(e) => setPatientForm({...patientForm, birth_date: e.target.value})}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Género</Label>
                <Select value={patientForm.gender} onValueChange={(v) => setPatientForm({...patientForm, gender: v})}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Seleccionar" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="masculino">Masculino</SelectItem>
                    <SelectItem value="femenino">Femenino</SelectItem>
                    <SelectItem value="otro">Otro</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Tipo de Sangre</Label>
                <Select value={patientForm.blood_type} onValueChange={(v) => setPatientForm({...patientForm, blood_type: v})}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Seleccionar" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="A+">A+</SelectItem>
                    <SelectItem value="A-">A-</SelectItem>
                    <SelectItem value="B+">B+</SelectItem>
                    <SelectItem value="B-">B-</SelectItem>
                    <SelectItem value="AB+">AB+</SelectItem>
                    <SelectItem value="AB-">AB-</SelectItem>
                    <SelectItem value="O+">O+</SelectItem>
                    <SelectItem value="O-">O-</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>Alergias</Label>
              <Textarea
                value={patientForm.allergies}
                onChange={(e) => setPatientForm({...patientForm, allergies: e.target.value})}
                placeholder="Ej: Penicilina, Aspirina..."
                className="mt-1"
                rows={2}
              />
            </div>

            <div>
              <Label>Condiciones Crónicas</Label>
              <Textarea
                value={patientForm.chronic_conditions}
                onChange={(e) => setPatientForm({...patientForm, chronic_conditions: e.target.value})}
                placeholder="Ej: Diabetes, Hipertensión..."
                className="mt-1"
                rows={2}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Contacto de Emergencia</Label>
                <Input
                  value={patientForm.emergency_contact}
                  onChange={(e) => setPatientForm({...patientForm, emergency_contact: e.target.value})}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Teléfono de Emergencia</Label>
                <Input
                  value={patientForm.emergency_phone}
                  onChange={(e) => setPatientForm({...patientForm, emergency_phone: e.target.value})}
                  className="mt-1"
                />
              </div>
            </div>

            <div>
              <Label>Notas Adicionales</Label>
              <Textarea
                value={patientForm.notes}
                onChange={(e) => setPatientForm({...patientForm, notes: e.target.value})}
                className="mt-1"
                rows={2}
              />
            </div>

            <Button onClick={savePatient} className="w-full btn-primary">
              <Save className="w-4 h-4 mr-2" />
              {editingPatient ? 'Actualizar' : 'Crear'} Paciente
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Medical Record Modal */}
      <Dialog open={showRecordModal} onOpenChange={setShowRecordModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Nueva Consulta - {selectedPatient?.first_name} {selectedPatient?.last_name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-6">
            {/* Vital Signs */}
            <div>
              <h4 className="font-medium mb-3 flex items-center gap-2">
                <Activity className="w-4 h-4" /> Signos Vitales
              </h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <Label className="text-xs">Peso (kg)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={recordForm.weight}
                    onChange={(e) => setRecordForm({...recordForm, weight: e.target.value})}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-xs">Altura (cm)</Label>
                  <Input
                    type="number"
                    value={recordForm.height}
                    onChange={(e) => setRecordForm({...recordForm, height: e.target.value})}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-xs">Presión Sistólica</Label>
                  <Input
                    type="number"
                    placeholder="120"
                    value={recordForm.blood_pressure_systolic}
                    onChange={(e) => setRecordForm({...recordForm, blood_pressure_systolic: e.target.value})}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-xs">Presión Diastólica</Label>
                  <Input
                    type="number"
                    placeholder="80"
                    value={recordForm.blood_pressure_diastolic}
                    onChange={(e) => setRecordForm({...recordForm, blood_pressure_diastolic: e.target.value})}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-xs">Pulso (bpm)</Label>
                  <Input
                    type="number"
                    value={recordForm.heart_rate}
                    onChange={(e) => setRecordForm({...recordForm, heart_rate: e.target.value})}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-xs">Temperatura (°C)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={recordForm.temperature}
                    onChange={(e) => setRecordForm({...recordForm, temperature: e.target.value})}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-xs">Saturación O2 (%)</Label>
                  <Input
                    type="number"
                    value={recordForm.oxygen_saturation}
                    onChange={(e) => setRecordForm({...recordForm, oxygen_saturation: e.target.value})}
                    className="mt-1"
                  />
                </div>
              </div>
            </div>

            {/* Consultation */}
            <div>
              <h4 className="font-medium mb-3 flex items-center gap-2">
                <Stethoscope className="w-4 h-4" /> Consulta
              </h4>
              <div className="space-y-4">
                <div>
                  <Label>Síntomas / Motivo de Consulta</Label>
                  <Textarea
                    value={recordForm.symptoms}
                    onChange={(e) => setRecordForm({...recordForm, symptoms: e.target.value})}
                    placeholder="Describe los síntomas del paciente..."
                    className="mt-1"
                    rows={2}
                  />
                </div>
                <div>
                  <Label>Diagnóstico</Label>
                  <Textarea
                    value={recordForm.diagnosis}
                    onChange={(e) => setRecordForm({...recordForm, diagnosis: e.target.value})}
                    placeholder="Diagnóstico médico..."
                    className="mt-1"
                    rows={2}
                  />
                </div>
                <div>
                  <Label>Tratamiento</Label>
                  <Textarea
                    value={recordForm.treatment}
                    onChange={(e) => setRecordForm({...recordForm, treatment: e.target.value})}
                    placeholder="Plan de tratamiento..."
                    className="mt-1"
                    rows={2}
                  />
                </div>
              </div>
            </div>

            {/* Prescription */}
            <div>
              <h4 className="font-medium mb-3 flex items-center gap-2">
                <Pill className="w-4 h-4" /> Receta Médica
              </h4>
              <Textarea
                value={recordForm.prescription}
                onChange={(e) => setRecordForm({...recordForm, prescription: e.target.value})}
                placeholder="Medicamento 1: Dosis, frecuencia, duración&#10;Medicamento 2: Dosis, frecuencia, duración"
                className="mt-1"
                rows={4}
              />
            </div>

            {/* Additional */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Resultados de Laboratorio</Label>
                <Textarea
                  value={recordForm.lab_results}
                  onChange={(e) => setRecordForm({...recordForm, lab_results: e.target.value})}
                  className="mt-1"
                  rows={2}
                />
              </div>
              <div>
                <Label>Notas Adicionales</Label>
                <Textarea
                  value={recordForm.notes}
                  onChange={(e) => setRecordForm({...recordForm, notes: e.target.value})}
                  className="mt-1"
                  rows={2}
                />
              </div>
            </div>

            <div>
              <Label>Fecha de Seguimiento</Label>
              <Input
                type="date"
                value={recordForm.follow_up_date}
                onChange={(e) => setRecordForm({...recordForm, follow_up_date: e.target.value})}
                className="mt-1 w-48"
              />
            </div>

            <Button onClick={saveRecord} className="w-full btn-primary">
              <Save className="w-4 h-4 mr-2" />
              Guardar Consulta
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DoctorDashboard;
